import { useQuery } from "@tanstack/react-query";
import { usersApi } from "./axios";
import {
  DataI,
  ServiceResponseI,
  UserI,
  UsersLoginI,
  UsersLoginResponseI,
  UserPropsI,
  UserByIdI,
  UserListI,
  ClientUserI,
} from "../../interfaces";
import { AxiosError } from "axios";
import { ValidateCurrentToken, LogoutSystem } from "../../helpers";

const getAllUsers = async ({
  pagination,
  sorting,
  globalFilter,
  client,
}: UserPropsI): Promise<ServiceResponseI<DataI<UserListI>>> => {
  try {
    const validateToken = ValidateCurrentToken();

    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const params = new URLSearchParams();

    params.append("page", (pagination!.pageIndex + 1).toString());
    params.append("limit", pagination!.pageSize.toString());
    globalFilter && params.append("search", globalFilter);
    sorting && params.append("sorting", JSON.stringify(sorting));
    client && params.append("client", client.toString());

    //headers: { 'Authorization': validateToken
    const { data } = await usersApi.get<ServiceResponseI<DataI<UserListI>>>(
      "",
      {
        params,
        headers: { Authorization: validateToken },
      },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const getUsersByClient = async (clientId: string) => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await usersApi.get<ServiceResponseI<ClientUserI[]>>(
      `/by-client/${clientId}`,
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const usersGetbyToken = async () => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await usersApi.get<ServiceResponseI<UserI>>("by-token", {
      headers: { Authorization: validateToken },
    });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const usersLoginPost = async (
  getForms: UsersLoginI,
): Promise<ServiceResponseI<UsersLoginResponseI>> => {
  try {
    const { data } = await usersApi.post<ServiceResponseI<UsersLoginResponseI>>(
      "/login",
      getForms,
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;

    if (obj?.status === 401) {
      LogoutSystem();
    }

    if (obj?.status === 402) {
      const message: string = (obj?.data as any)?.message ?? "";
      const code = message.replace("SUBSCRIPTION_BLOCKED:", "").trim();
      sessionStorage.setItem("subscription_blocked_code", code);
      window.location.href = "/subscription-blocked";
      throw new Error("SUBSCRIPTION_BLOCKED");
    }

    throw new Error((obj?.data as Error).message);
  }
};

export const userPost = async <T>(
  getForms: T,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await usersApi.post("", getForms, {
      headers: { Authorization: validateToken },
    });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const userPut = async <T>(
  id: number,
  updateForms: T,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await usersApi.put(`/${id}`, updateForms, {
      headers: { Authorization: validateToken },
    });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const userPutChangePassword = async <T>(
  updateForms: T,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await usersApi.put(`/change-password`, updateForms, {
      headers: { Authorization: validateToken },
    });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const userPutChangeAvatar = async (
  avatar: string,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await usersApi.put(
      `/change-avatar`,
      { avatar },
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const userDelete = async (
  id: number,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await usersApi.delete(`/${id}`, {
      headers: { Authorization: validateToken },
    });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const userById = async (
  id: number,
): Promise<ServiceResponseI<UserByIdI>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await usersApi.get<ServiceResponseI<UserByIdI>>(`/${id}`, {
      headers: { Authorization: validateToken },
    });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const userResetPassword = async (
  id: number,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await usersApi.get<ServiceResponseI<string>>(
      `/reset-password/${id}`,
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const getClientIdByToken = async (): Promise<
  ServiceResponseI<number | null>
> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await usersApi.get<ServiceResponseI<number | null>>(
      `/get-client-id-by-token`,
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const userSetStatus = async <T>(
  id: number,
  payload: T,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await usersApi.patch<ServiceResponseI<string>>(
      `/set-status/${id}`,
      payload,
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const useUsers = ({
  sorting,
  pagination,
  globalFilter,
  client,
}: UserPropsI) => {
  const usersQuery = useQuery(
    ["users", { pagination, sorting, globalFilter, client }],
    () => getAllUsers({ pagination, sorting, globalFilter, client }),
    {
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    },
  );

  return {
    usersQuery,
  };
};
